import { useState, useMemo } from "react";
import { X, Shield, FileText } from "lucide-react";
import { TOS_TEXT, PRIVACY_TEXT } from "@/lib/legalDocs";

type Tab = "tos" | "privacy";

const TABS: { id: Tab; label: string; sub: string; Icon: typeof Shield }[] = [
  { id: "tos",      label: "Terms",    sub: "of Service", Icon: FileText },
  { id: "privacy",  label: "Privacy",  sub: "Policy",     Icon: Shield },
];

const SOURCE: Record<Tab, string> = {
  tos: TOS_TEXT,
  privacy: PRIVACY_TEXT,
};

interface Props {
  open: boolean;
  onClose: () => void;
  initialTab?: Tab;
}

export function LegalReader({ open, onClose, initialTab = "tos" }: Props) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const blocks = useMemo(() => parseMarkdown(SOURCE[tab]), [tab]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Legal pack reader"
      aria-modal="true"
      /* `page-safe-top` keeps the card's "Legal pack" header clear of the
         notch / Dynamic Island. `my-6` alone left it 24px from the top, which
         is inside the ~59px inset on an iPhone with an island. The backdrop
         still paints full-bleed behind the status bar. */
      className="fixed inset-0 z-[110] flex flex-col anim-fade-up legal-reader-backdrop page-safe-top"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mx-auto my-6 flex flex-col w-full max-w-2xl flex-1 rounded-2xl overflow-hidden legal-reader-card"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 legal-reader-divider">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[hsl(var(--teal))]">
              Nexus Dex
            </p>
            <h2 className="text-base font-extrabold tracking-tight legal-reader-title">
              Legal pack
            </h2>
          </div>
          <button
            type="button"
            data-testid="button-close-legal-reader"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover-elevate flex items-center justify-center legal-reader-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-3 py-2 legal-reader-divider">
          {TABS.map(({ id, label, sub, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                type="button"
                data-testid={`tab-legal-${id}`}
                onClick={() => setTab(id)}
                className={`flex-1 rounded-lg px-2.5 py-2 hover-elevate flex flex-col items-start gap-0.5 transition-colors legal-reader-tab${active ? " is-active" : ""}`}
              >
                <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em]">
                  <Icon className="w-3 h-3" />
                  {label}
                </span>
                <span className="text-[10px] font-semibold opacity-80">{sub}</span>
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div
          className="flex-1 overflow-y-auto px-6 py-5"
          data-testid={`legal-reader-body-${tab}`}
          style={{ background: "hsl(var(--background))" }}
        >
          {/* select-text: the global native-shell CSS disables text selection
              app-wide; opt it back in here so a trainer can select and copy a
              clause of the Terms / Privacy docs. */}
          <div className="select-text max-w-prose mx-auto space-y-3 text-[13.5px] leading-relaxed text-foreground">
            {blocks}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 flex items-center justify-between legal-reader-footer">
          <p className="text-[11px] legal-reader-footer-text">
            Governed by NSW, Australia law. Globally enforceable.
          </p>
          <button
            type="button"
            onClick={onClose}
            data-testid="button-legal-reader-done"
            className="text-xs font-bold text-[hsl(var(--teal))] hover-elevate rounded-full px-3 py-1.5"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------------
   Tiny markdown renderer, sufficient for our three docs.
   Supports: h1 (#), h2 (##), h3 (###), bullet lists (- ), bold (**...**),
   horizontal rule (---), blank-line paragraph breaks, inline code (`...`),
   and pipe tables (privacy.md's purposes + retention tables).
   Links rendered as plain text, italic ignored on purpose.
   ---------------------------------------------------------------------------- */

/** A pipe-table row split into trimmed cells: `| a | b |` -> ["a", "b"]. */
function splitTableRow(line: string): string[] {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

/** The `| --- | :--: |` separator that marks the line above as a header row. */
function isTableSeparator(line: string | undefined): boolean {
  if (!line || !line.trim().startsWith("|")) return false;
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((c) => /^:?-{1,}:?$/.test(c));
}
function parseMarkdown(src: string): JSX.Element[] {
  const lines = src.split("\n");
  const out: JSX.Element[] = [];
  let i = 0;
  let key = 0;

  const renderInline = (s: string) => {
    // Strip surrounding em-dashes if any sneak in (defensive, brand rule).
    const parts: (string | JSX.Element)[] = [];
    const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(s)) !== null) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      const tok = m[0];
      if (tok.startsWith("**")) {
        parts.push(
          <strong key={`b${key++}`} className="font-bold text-foreground">
            {tok.slice(2, -2)}
          </strong>,
        );
      } else if (tok.startsWith("`")) {
        parts.push(
          <code
            key={`c${key++}`}
            className="px-1 py-0.5 rounded text-[12px] font-mono"
            style={{ background: "var(--surface-elev-2)", color: "hsl(174 70% 75%)" }}
          >
            {tok.slice(1, -1)}
          </code>,
        );
      }
      last = m.index + tok.length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i];

    if (line.trim() === "") { i++; continue; }
    if (line.startsWith("---")) {
      out.push(
        <hr key={`hr${key++}`} style={{ borderColor: "var(--surface-elev-border)" }} className="my-4" />
      );
      i++; continue;
    }

    // Headings
    if (line.startsWith("### ")) {
      out.push(
        <h3 key={`h3${key++}`} className="text-sm font-extrabold tracking-tight mt-4 mb-1 text-foreground">
          {renderInline(line.slice(4))}
        </h3>
      );
      i++; continue;
    }
    if (line.startsWith("## ")) {
      out.push(
        <h2 key={`h2${key++}`} className="text-base font-extrabold tracking-tight mt-5 mb-1.5 text-foreground">
          {renderInline(line.slice(3))}
        </h2>
      );
      i++; continue;
    }
    if (line.startsWith("# ")) {
      out.push(
        <h1 key={`h1${key++}`} className="text-lg font-extrabold tracking-tight mt-2 mb-2 text-foreground">
          {renderInline(line.slice(2))}
        </h1>
      );
      i++; continue;
    }

    // Bullet list, accumulate adjacent "- " lines
    if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      out.push(
        <ul key={`ul${key++}`} className="list-disc pl-5 space-y-1 text-foreground">
          {items.map((it, idx) => (
            <li key={idx}>{renderInline(it)}</li>
          ))}
        </ul>
      );
      continue;
    }

    // Pipe table: a header row, its `|---|` separator, then body rows.
    // Rendered inside an overflow-x container so a wide table scrolls itself
    // rather than forcing the reader body to scroll sideways on a phone.
    if (line.trim().startsWith("|") && isTableSeparator(lines[i + 1])) {
      const header = splitTableRow(line);
      i += 2; // consume the header and the separator
      const rows: string[][] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) {
        rows.push(splitTableRow(lines[i]));
        i++;
      }
      const cellBorder = "1px solid var(--surface-elev-border)";
      out.push(
        <div key={`tw${key++}`} className="my-3 overflow-x-auto">
          <table className="w-full text-left border-collapse text-[12px]">
            <thead>
              <tr>
                {header.map((h, idx) => (
                  <th
                    key={idx}
                    className="font-bold p-2 align-top text-foreground"
                    style={{ borderBottom: cellBorder, background: "var(--surface-elev-2)" }}
                  >
                    {renderInline(h)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((cells, rIdx) => (
                <tr key={rIdx}>
                  {cells.map((cell, cIdx) => (
                    <td key={cIdx} className="p-2 align-top text-foreground" style={{ borderBottom: cellBorder }}>
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Paragraph
    out.push(
      <p key={`p${key++}`} className="text-foreground">
        {renderInline(line)}
      </p>
    );
    i++;
  }

  return out;
}
